import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { LocalConfig, transformParameters } from 'utils';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, Loader, Select } from '@atlas/ui';

import PaymentListItem from 'src/components/ClientProfile/Payment/PaymentListItem';

import { showWithdrawalErrorWithSupport, submitWithdrawal } from './withdrawalUtils';

const validateAccountNumber = value => {
  const maxLength = 10;
  const minLength = 9;
  if (!value) return { message: 'required' };

  if (value.length < 9) {
    return { message: 'min_length', params: { ruleOption: minLength } };
  } else if (value.length > 10) {
    return { message: 'max_length', params: { ruleOption: maxLength } };
  } else {
    return null;
  }
};

const WithdrawalProntopagaPopup = ({ data = {}, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl } = data;
  const [loading, setLoading] = useState(true);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [fieldsData, setFieldsData] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectId = LocalConfig.get('projectId');

  const [accountNumberInput, setAccountNumberInput] = useState('');
  const [bankCodeInput, setBankCodeInput] = useState('');
  const [docTypeInput, setDocTypeInput] = useState('');
  const [accountTypeInput, setAccountTypeInput] = useState('');

  const { amount, country, currency, paymentAdapter } = parseWithdrawalQuery(redirectUrl);

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const requestUrl = useMemo(
    () =>
      `payment/v2/payments/${paymentAdapter}/parameters?project-id=${projectId}&country=${country}&param-names=BANK_CODE&param-names=ACCOUNT_TYPE${country === 'EC' ? `&param-names=DOC_TYPE` : ''}`,
    [country, paymentAdapter, projectId],
  );

  useEffect(() => {
    const getFieldsData = prepareRequest({
      method: 'GET',
      url: requestUrl,
    });

    setLoading(true);
    getFieldsData()
      .then(res => {
        const parsedData = transformParameters(res.paymentParametersList);
        setFieldsData(parsedData);
      })
      .catch(() => {
        showWithdrawalErrorWithSupport();
        handleClose();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleClose, requestUrl]);

  const changeAccountNumber = useCallback(event => {
    const value = event.target.value.replace(/\D/g, '');

    const error = validateAccountNumber(value);

    setError(error);
    setAccountNumberInput(value);
  }, []);

  const handleClickButton = useCallback(async () => {
    const requestData = {
      additionalParameters: {
        accountNumber: accountNumberInput,
        accountType: accountTypeInput,
        bankCode: bankCodeInput,
        docType: docTypeInput,
        publicPaymentId: publicPaymentId,
      },
      amount: amount,
      currency: currency,
      pmId: pmId,
    };

    setIsSubmitting(true);

    try {
      await submitWithdrawal({ amount, currency, paymentMethod, requestData });
    } catch {
      showWithdrawalErrorWithSupport();
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  }, [
    accountNumberInput,
    accountTypeInput,
    amount,
    bankCodeInput,
    currency,
    docTypeInput,
    handleClose,
    paymentMethod,
    pmId,
    publicPaymentId,
  ]);

  return (
    <DefaultPopup
      className={cx('payment-popup', 'full-height')}
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="payment-popup-content">
        <div className="popup-payment-method">
          <PaymentListItem amount={amount} payment={paymentMethod} showAmount />
        </div>

        {loading && <Loader className={cx('form-loader', { 'loader-long': country === 'EC' })} />}

        {!loading && (
          <>
            <div className="payment-popup-block">
              <Input
                active={isActiveInput}
                className="popup-input"
                error={error}
                inputMode="numeric"
                label="prontopaga_account_number"
                maxLength={10}
                onBlur={() => setIsActiveInput(false)}
                onChange={changeAccountNumber}
                onFocus={() => setIsActiveInput(true)}
                type="number"
                value={accountNumberInput}
              />
              {error && (
                <div className="help-text-container">
                  <Label message={error.message} params={error.params} />
                </div>
              )}
            </div>
            {fieldsData['ACCOUNT_TYPE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_prontopaga_account_type'}
                className="payment-popup-select"
                label="withdrawal_prontopaga_account_type"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setAccountTypeInput}
                onFocus={() => setIsActiveSelect('withdrawal_prontopaga_account_type')}
                options={fieldsData['ACCOUNT_TYPE']}
              />
            )}
            {fieldsData['BANK_CODE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_prontopaga_bank_code'}
                className="payment-popup-select"
                label="withdrawal_prontopaga_bank_code"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setBankCodeInput}
                onFocus={() => setIsActiveSelect('withdrawal_prontopaga_bank_code')}
                options={fieldsData['BANK_CODE']}
              />
            )}
            {fieldsData['DOC_TYPE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_prontopaga_doc_type'}
                className="payment-popup-select"
                label="withdrawal_prontopaga_doc_type"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setDocTypeInput}
                onFocus={() => setIsActiveSelect('withdrawal_prontopaga_doc_type')}
                options={fieldsData['DOC_TYPE']}
              />
            )}
          </>
        )}
      </div>

      {!loading && (
        <div className="popup__footer">
          <Button
            className="primary big block"
            disabled={!accountNumberInput || isSubmitting || error}
            error={error}
            label="withdrawal"
            loading={isSubmitting}
            onClick={handleClickButton}
          />
        </div>
      )}
    </DefaultPopup>
  );
};

export default WithdrawalProntopagaPopup;
